import { useAppSelector } from '@/hooks';
import { isInIframe } from '@/misc/common';
import { debounce } from 'lodash';
import React, { ReactNode, useEffect, useState, useCallback, useRef } from 'react';
import BEMHelper from 'react-bem-helper';
import Portal from "../elements/Portal";
import "./StickyBottom.css";

const c = new BEMHelper("StickyBottom");

interface MyComponentProps {
    render: (sticky: boolean) => ReactNode;
    sticky: boolean;
    wrapperProps?: Omit<React.ComponentPropsWithoutRef<'div'>, 'ref'>;
}

const mediaQuery = '(max-width: 900px) and (orientation: portrait)';
const mediaQueryList = window.matchMedia(mediaQuery);


const DEFAULT_TRANSFORM_STRING = 'translateY(0px)';
const HEIGHT_RATIO_STICKY_TOLERANCE = 0.6;

const StickyBottom: React.FC<MyComponentProps> = ({
    render,
    sticky,
    wrapperProps = {}, // Default to an empty object for safe destructuring
}) => {
    const safeWrapperProps = wrapperProps;
    const [responsiveSticky, setResponsiveSticky] = useState(false);
    const [transformString, setTransformString] = useState(DEFAULT_TRANSFORM_STRING);
    const portalRef = useRef<HTMLDivElement | null>(null);
    const appBottom = useAppSelector(s => {
        if (isInIframe()) {
            return s.ui.parentScrollTop;
        }

        return 0;
    });
    const isPortrait = useAppSelector(s => s.ui.isPortrait);

    const debounceUpdateTransformString = useCallback(
        debounce((appBottom) => {
            setTransformString(`translateY(${-appBottom}px)`);
        }, 600),
        []
    );

    const handlePortraitChange = useCallback((event: MediaQueryList | MediaQueryListEvent) => {
        if (event.matches && isPortrait) {
            setResponsiveSticky(true);
        } else {
            setResponsiveSticky(false);
        }
    }, [sticky, responsiveSticky, isPortrait]);

    useEffect(() => {
        mediaQueryList.addEventListener('change', handlePortraitChange);
        handlePortraitChange(mediaQueryList);
        return () => {
            mediaQueryList.removeEventListener('change', handlePortraitChange)
        }
    }, []);


    useEffect(() => {
        if ((document.body.getBoundingClientRect().height * HEIGHT_RATIO_STICKY_TOLERANCE) - appBottom > 0 && isPortrait) {
            setResponsiveSticky(true);
        } else {
            setResponsiveSticky(false);
        }
    }, [sticky, appBottom]);


    useEffect(() => {
        setTransformString(DEFAULT_TRANSFORM_STRING);
        if (responsiveSticky) {
            debounceUpdateTransformString(appBottom);
        }
    }, [responsiveSticky, appBottom]);

    const wrapContent = (_sticky: boolean): ReactNode => {
        if (_sticky) {
            return (
                <Portal
                    ref={portalRef}
                    wrapperSelector="#bokamera-embedded"
                    style={{ transform: transformString }}
                    {...safeWrapperProps}
                    {...c()}
                >
                    {render(_sticky)}
                </Portal>
            );
        }

        return render(_sticky);
    };

    const shouldPortal = sticky && responsiveSticky;

    return (
        <>
            {shouldPortal && <div>{render(false)}</div>}

            {wrapContent(shouldPortal)}
        </>
    );
};

export default StickyBottom;
